import { Dispatch, SetStateAction, useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@tamagui/toast'
import { ICustomProductListItem } from '@dtos/MembersConfig'
import { GET_PRODUCTS_CUSTOM_LIST } from '@services/api/graphql/queries/getProductsCustomList'

export const UNLOCK_POLL_INTERVAL_MS = 3000
const UNLOCK_POLL_ATTEMPTS = 10

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// O pagamento aprovado vira matrícula no core de forma assíncrona (sparkecommerce →
// create_enrollment). A lista guarda os itens em estado local, então o card só destrava
// quando a matrícula aparece: relê a posição do item até ele chegar liberado.
export function useUnlockPurchasedItem({
  listId,
  items,
  onUnlocked,
}: {
  listId: string
  items: ICustomProductListItem[]
  onUnlocked: Dispatch<SetStateAction<ICustomProductListItem[]>>
}) {
  const client = useApolloClient()
  const toast = useToastController()
  const { t } = useTranslation('DirectPurchase')

  // Uma leitura: se o item já chegou liberado, troca o card e avisa o aluno.
  const checkUnlocked = useCallback(
    async (item: ICustomProductListItem) => {
      const offset = items.findIndex((i) => i.id === item.id)
      if (offset < 0) return false

      try {
        const { data } = await client.query({
          query: GET_PRODUCTS_CUSTOM_LIST,
          variables: { id: listId, offset, limit: 1 },
          fetchPolicy: 'network-only',
        })
        const fresh: ICustomProductListItem | undefined =
          data?.membersConfig?.customProductLists?.[0]?.items?.[0]
        if (fresh?.id !== item.id || !fresh.activeForUser) return false

        onUnlocked((current) =>
          current.map((i) => (i.id === item.id ? fresh : i)),
        )
        toast.show(t('toast.title'), { message: t('toast.unlocked') })
        return true
      } catch {
        return false
      }
    },
    [client, items, listId, onUnlocked, t, toast],
  )

  // Insiste até a matrícula aparecer.
  const unlock = useCallback(
    async (item: ICustomProductListItem) => {
      for (let attempt = 0; attempt < UNLOCK_POLL_ATTEMPTS; attempt++) {
        if (await checkUnlocked(item)) return
        await wait(UNLOCK_POLL_INTERVAL_MS)
      }

      toast.show(t('toast.title'), { message: t('toast.unlocking') })
    },
    [checkUnlocked, t, toast],
  )

  return unlock
}
